<template>
  <a-button
    ref="googleLoginButton"
    size="large"
    :disabled="!client || disabled"
    class="google-login-button switch-mode-btn"
    @click="handleClick"
  >
    <Resource
      class="w-[24px] h-[24px] icon-google"
      :src="getFeUrl('/youyan-platform/png/icon-google.png')"
      :transform-resource="transformImage"
    ></Resource>
    {{ $t('continue-with-google') }}
  </a-button>
</template>

<script lang="ts" setup>
import { omit } from 'lodash-es';
import { getUserInternationalLoginAuthorize, postUserInternationalOauth2Login } from '~/api';
import { withRegisterOrLoginChannelForUs } from '~/api/module/open-login';
import { getLocation } from '~/api/module/seo';
import { transformImage } from '~/utils/';
import { TRACK_EVENT, trackEvent } from '~/utils/track';
import { getFeUrl } from '~/utils/url';

defineProps<{ disabled?: boolean }>();
const emits = defineEmits(['success']);

const googleLoginButton = ref<HTMLElement | null>(null);
const client = ref<any>();

const handleClick = async () => {
  trackEvent(TRACK_EVENT.LOGIN_GOOGLE_CLICK);
  await initialize();
  if (client.value) {
    client.value.requestCode();
  }
};

const loadScript = () => {
  return new Promise<void>(resolve => {
    if (window.google) {
      return resolve();
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
};

const initialize = async () => {
  const [locationRes, googleOauthRes] = await concurrency([
    getLocation(),
    getUserInternationalLoginAuthorize({
      service: 'google',
    }),
    loadScript(),
  ]);

  if (googleOauthRes) {
    client.value = window.google.accounts.oauth2.initCodeClient({
      ...omit(googleOauthRes.data, 'nonce'),
      callback: async (revocationResponse: any) => {
        try {
          const res = await postUserInternationalOauth2Login(
            withRegisterOrLoginChannelForUs({
              code: revocationResponse.code,
              state: revocationResponse.state,
              service: 'google',
              country: locationRes.country,
            }),
          );
          trackEvent(TRACK_EVENT.GOOGLE_LOGIN_SUCCESS);
          emits('success', res.data);
        } catch (err: any) {
          console.error(err);
          // 埋点：Google登录失败
          trackEvent(TRACK_EVENT.LOGIN_GOOGLE_FAIL, {
            error_code: err?.code || err?.error_code || 'unknown',
            error_msg: err?.msg || err?.error_reason || err?.message || '登录失败',
          });
        }
      },
    });
  }
};

onMounted(initialize);
</script>

<style lang="scss" scoped>
.google-login-button {
  position: relative;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    border-color: var(--back-6);
    color: var(--text-1);
  }
  &:disabled {
    background-color: transparent;
    color: var(--text-1-40);
  }
}
.icon-google {
  position: absolute;
  left: 12px;
}
</style>
